import { z } from 'zod';
import { Id, CanonicalId, Digest, parse } from './contracts.mjs';
import { requireCondition } from './errors.mjs';

export const PublicInspectManifest = z.strictObject({
  schemaVersion: z.literal(1),
  buildId: Id,
  revisionId: CanonicalId,
  manifestDigest: Digest,
  targets: z
    .array(
      z.strictObject({
        pid: Id,
        targetId: Id,
        label: z.string().min(1).max(200),
        parentPid: Id.nullable(),
      }),
    )
    .max(50000),
});

// Browser-safe, read-only projection of server-authorized labels and hierarchy.
// A lookup never spends a hover request or authorizes selection, source or edit.
export function createInspectManifestView(raw, expected) {
  const projection = parse(PublicInspectManifest, raw);
  for (const field of ['buildId', 'revisionId', 'manifestDigest'])
    requireCondition(projection[field] === expected[field], 'manifest_binding_mismatch');
  const byPid = new Map();
  const byTarget = new Map();
  for (const target of projection.targets) {
    requireCondition(
      !byPid.has(target.pid) && !byTarget.has(target.targetId),
      'duplicate_manifest_target',
    );
    byPid.set(target.pid, target);
    byTarget.set(target.targetId, target.pid);
  }
  validateParents(byPid);
  return Object.freeze({
    list: () => projection.targets.map(({ pid, label }) => ({ targetId: pid, label })),
    label: (pid) => byPid.get(pid)?.label ?? null,
    targetIdFor: (pid) => byPid.get(pid)?.targetId ?? null,
    nodeIdFor: (targetId) => byTarget.get(targetId) ?? null,
    chain(pid) {
      const chain = [];
      for (let current = byPid.get(pid); current; current = byPid.get(current.parentPid))
        chain.push(current.pid);
      return chain;
    },
  });
}

function validateParents(byPid) {
  const done = new Set();
  for (const pid of byPid.keys()) {
    const path = new Set();
    let current = pid;
    while (current !== null && !done.has(current)) {
      requireCondition(byPid.has(current), 'manifest_parent_unavailable');
      requireCondition(!path.has(current), 'manifest_parent_cycle');
      path.add(current);
      current = byPid.get(current).parentPid;
    }
    for (const node of path) done.add(node);
  }
}
